import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/hooks/useLanguage';
import { FOUNDER_MESSAGE } from '@/data/index';
import { IMAGES } from '@/assets/images';
import { Globe, ShieldCheck, Languages } from 'lucide-react';

/**
 * FounderMessage Page
 * Highlights the multicultural background and professional commitment of Karei Chin.
 * Adheres to the Japanese corporate minimalist design system with Deep Green and Rice White.
 */
const FounderMessage: React.FC = () => {
  const { t } = useLanguage();

  // Animation variants for staggered entry
  const containerVariants = { 
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <div className="bg-background min-h-screen">
      {/* Hero Header Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
              {t({ jp: '代表挨拶', en: 'Founder Message' })}
            </h1>
            <div className="h-1.5 w-24 bg-primary mb-8" />
            <p className="text-xl text-muted-foreground max-w-2xl leading-relaxed">
              {t({
                jp: '日本・中国・英語圏を繋ぐ架け橋として、文化の多様性を尊重した持続可能なビジネスを追求します。',
                en: 'As a bridge between Japan, China, and the West, we pursue sustainable business that respects cultural diversity.',
              })}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Message Text Area */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-12"
            >
              <motion.div variants={itemVariants} className="space-y-4">
                <p className="text-sm font-bold text-primary tracking-widest uppercase">
                  {t({ jp: FOUNDER_MESSAGE.role.ja, en: FOUNDER_MESSAGE.role.en })}
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                  {t({ jp: FOUNDER_MESSAGE.name.ja, en: FOUNDER_MESSAGE.name.en })}
                </h2>
              </motion.div>

              <motion.div variants={itemVariants} className="space-y-8 text-lg leading-loose text-foreground/80">
                {FOUNDER_MESSAGE.content.map((paragraph, index) => (
                  <p key={index} className="font-normal">
                    {t({ jp: paragraph.ja, en: paragraph.en })}
                  </p>
                ))}
              </motion.div>

              {/* Expertise Highlights */}
              <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8">
                <div className="p-6 bg-card border border-border rounded-lg">
                  <Languages className="w-8 h-8 text-primary mb-4" />
                  <h3 className="font-bold mb-2">
                    {t({ jp: '多言語能力', en: 'Trilingual' })}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t({ jp: '日・中・英での円滑な交渉', en: 'Fluent negotiation in JP/CN/EN' })}
                  </p>
                </div>
                <div className="p-6 bg-card border border-border rounded-lg">
                  <Globe className="w-8 h-8 text-primary mb-4" />
                  <h3 className="font-bold mb-2">
                    {t({ jp: '文化理解', en: 'Cultural Insight' })}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t({ jp: '市場ごとの商習慣を熟知', en: 'Expertise in regional customs' })}
                  </p>
                </div>
                <div className="p-6 bg-card border border-border rounded-lg">
                  <ShieldCheck className="w-8 h-8 text-primary mb-4" />
                  <h3 className="font-bold mb-2">
                    {t({ jp: '法令遵守', en: 'Compliance' })}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t({ jp: '日本法人の透明な運営', en: 'Transparent JP operations' })}
                  </p>
                </div>
              </motion.div>
            </motion.div>

            {/* Visual Column */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative sticky top-32"
            >
              <div className="aspect-[4/5] rounded-xl overflow-hidden shadow-2xl grayscale hover:grayscale-0 transition-all duration-700">
                <img
                  src={IMAGES.OFFICE_BUILDING_4}
                  alt="M.M.K Executive Presence"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Signature Quote Card */}
              <div className="absolute -bottom-8 -left-8 md:bottom-12 md:-left-12 bg-primary text-primary-foreground p-8 md:p-10 rounded-lg shadow-xl max-w-sm">
                <p className="text-xl md:text-2xl font-medium leading-tight italic">
                  {t({
                    jp: '「文化の違いを壁にせず、新たな価値を創出する力に変える。」',
                    en: '"Transforming cultural differences from barriers into engines of value creation."',
                  })}
                </p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="h-px w-8 bg-primary-foreground/50" />
                  <span className="text-sm font-bold tracking-widest uppercase opacity-80">
                    {t({ jp: FOUNDER_MESSAGE.name.ja, en: FOUNDER_MESSAGE.name.en })}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-2xl font-bold text-primary">
              {t({ jp: '私たちの使命', en: 'Our Mission' })}
            </h2>
            <p className="text-2xl md:text-3xl font-medium leading-relaxed text-foreground">
              {t({
                jp: '私たちは、単なるビジネスの仲介者ではありません。誠実さと専門性を持ち、異なる文化背景を持つパートナーと共に、持続可能な未来を築くための「真のローカライズ」を提供します。',
                en: 'We are not just business intermediaries. With integrity and expertise, we provide "true localization" to build a sustainable future alongside partners from diverse cultural backgrounds.',
              })}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FounderMessage;